// import React, { useState, useEffect, useRef } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { CheckCircle, Loader, Search, Filter, Sparkles, BarChart, Database, Trophy, AlertCircle } from "lucide-react";

// // Tips that rotate during analysis to keep users engaged
// const ANALYSIS_TIPS = [
//   {
//     icon: <Search className="text-blue-500" />,
//     title: "Scanning conversations",
//     text: "We're analyzing thousands of online conversations to find potential customers.",
//   },
//   {
//     icon: <Database className="text-indigo-500" />,
//     title: "Processing data",
//     text: "Our AI is extracting intent signals and matching them to your product description.",
//   },
//   {
//     icon: <Filter className="text-purple-500" />,
//     title: "Filtering results",
//     text: "We're removing low-quality matches to ensure you only see relevant leads.",
//   },
//   {
//     icon: <BarChart className="text-green-500" />,
//     title: "Scoring relevance",
//     text: "Each potential lead is being scored based on intent and fit with your product.",
//   },
//   {
//     icon: <Sparkles className="text-amber-500" />,
//     title: "Generating responses",
//     text: "Our AI is crafting personalized response suggestions for each lead.",
//   },
// ];

// // Analysis steps to show progress
// const ANALYSIS_STEPS = [
//   { label: "Collecting data", completeAt: 25 },
//   { label: "Processing conversations", completeAt: 50 },
//   { label: "Analyzing intent", completeAt: 75 },
//   { label: "Scoring and filtering", completeAt: 85 },
//   { label: "Generating responses", completeAt: 95 },
//   { label: "Finalizing results", completeAt: 100 },
// ];

// const AnalysisProgressModal = ({ isOpen, progress, platform = "reddit", onClose = () => {} }) => {
//   const [currentTipIndex, setCurrentTipIndex] = useState(0);
//   const [isComplete, setIsComplete] = useState(false);
//   const [showError, setShowError] = useState(false);
//   const timeoutRef = useRef(null);

//   // Platform-specific styling
//   const platformColor = platform === "twitter" ? "blue" : "green";
//   const platformTextColor = `text-${platformColor}-600`;
//   const platformBgColor = `bg-${platformColor}-500`;
//   const platformBgLightColor = `bg-${platformColor}-50`;
//   const platformBorderColor = `border-${platformColor}-200`;
//   const platformPulseColor = `pulse-${platformColor}`;

//   // Rotate tips every 8 seconds to keep users engaged
//   useEffect(() => {
//     if (progress < 100 && isOpen) {
//       const interval = setInterval(() => {
//         setCurrentTipIndex((prev) => (prev + 1) % ANALYSIS_TIPS.length);
//       }, 8000);

//       return () => clearInterval(interval);
//     }
//   }, [progress, isOpen]);

//   // Handle completion animation and auto-close after delay
//   useEffect(() => {
//     if (progress >= 100 && isOpen) {
//       setIsComplete(true);

//       // Auto-close after showing completion for 2.5 seconds
//       timeoutRef.current = setTimeout(() => {
//         onClose();
//       }, 2500);
//     }

//     return () => {
//       if (timeoutRef.current) {
//         clearTimeout(timeoutRef.current);
//       }
//     };
//   }, [progress, isOpen, onClose]);

//   // Show timeout error if process takes too long (over 5 minutes)
//   useEffect(() => {
//     if (progress < 95 && isOpen) {
//       const errorTimeout = setTimeout(() => {
//         setShowError(true);
//       }, 5 * 60 * 1000); // 5 minutes

//       return () => clearTimeout(errorTimeout);
//     }
//   }, [progress, isOpen]);

//   // Find the current step
//   const currentStep = ANALYSIS_STEPS.findIndex((step) => progress < step.completeAt);
//   const activeStep = currentStep === -1 ? ANALYSIS_STEPS.length - 1 : currentStep;

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
//       <motion.div
//         initial={{ opacity: 0, scale: 0.9 }}
//         animate={{ opacity: 1, scale: 1 }}
//         exit={{ opacity: 0, scale: 0.9 }}
//         transition={{ type: "spring", damping: 30, stiffness: 500 }}
//         className="bg-white rounded-xl p-8 max-w-lg w-full shadow-xl"
//       >
//         {/* Header */}
//         <div className="text-center mb-8">
//           {isComplete ? (
//             <motion.div
//               initial={{ scale: 0, opacity: 0 }}
//               animate={{ scale: 1, opacity: 1 }}
//               transition={{ type: "spring", damping: 20, stiffness: 300, delay: 0.1 }}
//             >
//               <div className={`w-20 h-20 rounded-full ${platformBgLightColor} flex items-center justify-center mx-auto mb-4`}>
//                 <Trophy className={`w-10 h-10 ${platformTextColor}`} />
//               </div>
//               <h2 className="text-2xl font-bold text-gray-800 mb-2">Analysis Complete!</h2>
//               <p className="text-gray-600">We've found the best matches for your product!</p>
//             </motion.div>
//           ) : showError ? (
//             <div>
//               <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
//                 <AlertCircle className="w-10 h-10 text-red-500" />
//               </div>
//               <h2 className="text-xl font-bold text-gray-800 mb-2">Taking longer than expected</h2>
//               <p className="text-gray-600 mb-6">The analysis is still running but taking longer than usual.</p>
//               <div className="flex justify-center space-x-4">
//                 <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800 transition-colors">
//                   Cancel
//                 </button>
//                 <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors">
//                   Continue waiting
//                 </button>
//               </div>
//             </div>
//           ) : (
//             <>
//               <div className="relative w-28 h-28 mx-auto mb-6">
//                 {/* Outer circular progress */}
//                 <svg className="w-full h-full" viewBox="0 0 100 100">
//                   <circle cx="50" cy="50" r="45" fill="none" stroke="#f0f0f0" strokeWidth="8" />
//                   <motion.circle
//                     cx="50"
//                     cy="50"
//                     r="45"
//                     fill="none"
//                     stroke={platform === "twitter" ? "#3b82f6" : "#10b981"}
//                     strokeWidth="8"
//                     strokeLinecap="round"
//                     initial={{ pathLength: 0 }}
//                     animate={{ pathLength: progress / 100 }}
//                     transition={{ duration: 0.5, ease: "easeInOut" }}
//                     style={{
//                       rotate: -90,
//                       transformOrigin: "center",
//                     }}
//                   />
//                 </svg>

//                 {/* Inner content with animated dots */}
//                 <div className="absolute inset-0 flex flex-col items-center justify-center">
//                   <span className={`text-2xl font-bold ${platformTextColor}`}>{Math.round(progress)}%</span>
//                   <div className="flex space-x-1 mt-1">
//                     <motion.div
//                       animate={{ scale: [1, 1.2, 1] }}
//                       transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.2 }}
//                       className={`w-1.5 h-1.5 rounded-full ${platformBgColor}`}
//                     />
//                     <motion.div
//                       animate={{ scale: [1, 1.2, 1] }}
//                       transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.2, delay: 0.3 }}
//                       className={`w-1.5 h-1.5 rounded-full ${platformBgColor}`}
//                     />
//                     <motion.div
//                       animate={{ scale: [1, 1.2, 1] }}
//                       transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.2, delay: 0.6 }}
//                       className={`w-1.5 h-1.5 rounded-full ${platformBgColor}`}
//                     />
//                   </div>
//                 </div>
//               </div>

//               <h2 className="text-xl font-bold text-gray-800 mb-1">{platform === "twitter" ? "Analyzing Twitter" : "Analyzing Reddit"}</h2>
//               <p className="text-gray-500 text-sm mb-2">Finding potential customers for your product</p>

//               {/* Time estimate */}
//               <p className="text-gray-400 text-xs flex items-center justify-center">
//                 <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//                   <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
//                   <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
//                 </svg>
//                 Estimated time: {Math.max(1, Math.ceil(3 * (1 - progress / 100)))} minute
//                 {Math.ceil(3 * (1 - progress / 100)) !== 1 ? "s" : ""} remaining
//               </p>
//             </>
//           )}
//         </div>

//         {!isComplete && !showError && (
//           <>
//             {/* Analysis steps */}
//             <div className="mb-8">
//               {ANALYSIS_STEPS.map((step, index) => (
//                 <div key={index} className="flex items-center mb-3 last:mb-0">
//                   <div
//                     className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 transition-colors duration-300
//                       ${index < activeStep ? "bg-green-500" : index === activeStep ? platformBgLightColor : "bg-gray-100"}`}
//                   >
//                     {index < activeStep ? (
//                       <CheckCircle size={16} className="text-white" />
//                     ) : index === activeStep ? (
//                       <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
//                         <Loader size={14} className={platformTextColor} />
//                       </motion.div>
//                     ) : (
//                       <div className="w-2 h-2 bg-gray-300 rounded-full" />
//                     )}
//                   </div>
//                   <div className="flex-1">
//                     <div className="flex justify-between items-center">
//                       <span
//                         className={`text-sm font-medium
//                           ${index < activeStep ? "text-green-600" : index === activeStep ? platformTextColor : "text-gray-400"}
//                         `}
//                       >
//                         {step.label}
//                       </span>
//                       {index < activeStep && <span className="text-xs text-green-500">Done</span>}
//                     </div>

//                     {/* Progress bar for current step */}
//                     {index === activeStep && (
//                       <div className="mt-1 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
//                         <motion.div
//                           className={platformBgColor}
//                           initial={{ width: 0 }}
//                           animate={{
//                             width: `${Math.min(
//                               100,
//                               ((progress - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0)) /
//                                 (step.completeAt - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0))) *
//                                 100
//                             )}%`,
//                           }}
//                           transition={{ duration: 0.5 }}
//                         />
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               ))}
//             </div>

//             {/* Rotating tips */}
//             <AnimatePresence mode="wait">
//               <motion.div
//                 key={currentTipIndex}
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 transition={{ duration: 0.3 }}
//                 className={`p-4 ${platformBgLightColor} rounded-lg ${platformBorderColor} border`}
//               >
//                 <div className="flex items-start">
//                   <div className="mr-3 mt-1">{ANALYSIS_TIPS[currentTipIndex].icon}</div>
//                   <div>
//                     <h4 className={`font-medium ${platformTextColor} mb-1`}>{ANALYSIS_TIPS[currentTipIndex].title}</h4>
//                     <p className="text-gray-600 text-sm">{ANALYSIS_TIPS[currentTipIndex].text}</p>
//                   </div>
//                 </div>
//               </motion.div>
//             </AnimatePresence>
//           </>
//         )}
//       </motion.div>
//     </div>
//   );
// };

// export default React.memo(AnalysisProgressModal);
// import React, { useState, useEffect, useRef } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import {
//   CheckCircle,
//   Loader,
//   Search,
//   Filter,
//   Sparkles,
//   BarChart,
//   Database,
//   Trophy,
//   AlertCircle,
//   RefreshCw,
//   Edit,
//   MessageSquare,
//   ArrowRight,
//   Target,
//   Twitter,
// } from "lucide-react";

// // Tips that rotate during analysis to keep users engaged
// const ANALYSIS_TIPS = [
//   {
//     icon: <Search className="text-blue-500" />,
//     title: "Scanning conversations",
//     text: "We're analyzing thousands of online conversations to find potential customers.",
//   },
//   {
//     icon: <Database className="text-indigo-500" />,
//     title: "Processing data",
//     text: "Our AI is extracting intent signals and matching them to your product description.",
//   },
//   {
//     icon: <Filter className="text-purple-500" />,
//     title: "Filtering results",
//     text: "We're removing low-quality matches to ensure you only see relevant leads.",
//   },
//   {
//     icon: <BarChart className="text-green-500" />,
//     title: "Scoring relevance",
//     text: "Each potential lead is being scored based on intent and fit with your product.",
//   },
//   {
//     icon: <Sparkles className="text-amber-500" />,
//     title: "Generating responses",
//     text: "Our AI is crafting personalized response suggestions for each lead.",
//   },
// ];

// // Analysis steps to show progress
// const ANALYSIS_STEPS = [
//   { label: "Collecting data", completeAt: 25 },
//   { label: "Processing conversations", completeAt: 50 },
//   { label: "Analyzing intent", completeAt: 75 },
//   { label: "Scoring and filtering", completeAt: 85 },
//   { label: "Generating responses", completeAt: 95 },
//   { label: "Finalizing results", completeAt: 100 },
// ];

// const AnalysisProgressModal = ({
//   isOpen,
//   progress,
//   platform = "reddit",
//   newPostsCount = null,
//   remainingCredits = null,
//   onClose = () => {},
//   onRetry = () => {}, // Callback to retry the analysis
//   onEditKeywords = () => {}, // Callback to edit keywords
// }) => {
//   const [currentTipIndex, setCurrentTipIndex] = useState(0);
//   const [isComplete, setIsComplete] = useState(false);
//   const [showError, setShowError] = useState(false);
//   const timeoutRef = useRef(null);
//   const errorTimeoutRef = useRef(null);

//   // Platform-specific styling
//   const platformColor = platform === "twitter" ? "blue" : "green";
//   const platformIcon = platform === "twitter" ? <Twitter size={24} /> : <Target size={24} />;
//   const platformName = platform === "twitter" ? "Twitter" : "Reddit";
//   const platformTextColor = `text-${platformColor}-600`;
//   const platformBgColor = `bg-${platformColor}-500`;
//   const platformHoverBgColor = `hover:bg-${platformColor}-600`;
//   const platformBgLightColor = `bg-${platformColor}-50`;
//   const platformBorderColor = `border-${platformColor}-200`;

//   // Rotate tips every 8 seconds to keep users engaged
//   useEffect(() => {
//     if (progress < 100 && isOpen) {
//       const interval = setInterval(() => {
//         setCurrentTipIndex((prev) => (prev + 1) % ANALYSIS_TIPS.length);
//       }, 8000);
//       return () => clearInterval(interval);
//     }
//   }, [progress, isOpen]);

//   // Handle completion animation and auto-close after delay
//   useEffect(() => {
//     if (progress >= 100 && isOpen) {
//       setIsComplete(true);

//       // Only auto-close when results were found
//       if (newPostsCount > 0) {
//         timeoutRef.current = setTimeout(onClose, 3000);
//       }
//     }

//     return () => {
//       if (timeoutRef.current) {
//         clearTimeout(timeoutRef.current);
//       }
//     };
//   }, [progress, isOpen, onClose, newPostsCount]);

//   // Show timeout error if process takes too long (over 5 minutes)
//   useEffect(() => {
//     if (progress < 95 && isOpen) {
//       errorTimeoutRef.current = setTimeout(() => setShowError(true), 5 * 60 * 1000);
//     }

//     return () => {
//       if (errorTimeoutRef.current) {
//         clearTimeout(errorTimeoutRef.current);
//       }
//     };
//   }, [progress, isOpen]);

//   // Determine active step for progress bar
//   const currentStep = ANALYSIS_STEPS.findIndex((step) => progress < step.completeAt);
//   const activeStep = currentStep === -1 ? ANALYSIS_STEPS.length - 1 : currentStep;

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
//       <motion.div
//         initial={{ opacity: 0, scale: 0.9 }}
//         animate={{ opacity: 1, scale: 1 }}
//         exit={{ opacity: 0, scale: 0.9 }}
//         transition={{ type: "spring", damping: 30, stiffness: 500 }}
//         className="bg-white rounded-xl p-8 max-w-lg w-full shadow-xl"
//       >
//         {/* Header with dynamic content based on state */}
//         <div className="text-center mb-8">
//           {isComplete && newPostsCount === 0 ? (
//             // No Results Found State
//             <motion.div
//               initial={{ scale: 0, opacity: 0 }}
//               animate={{ scale: 1, opacity: 1 }}
//               transition={{ type: "spring", damping: 20, stiffness: 300, delay: 0.1 }}
//               className="flex flex-col items-center"
//             >
//               <div className="w-24 h-24 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
//                 <Search className="w-12 h-12 text-amber-500" />
//               </div>
//               <h2 className="text-2xl font-bold text-gray-800 mb-2">No Leads Found</h2>
//               <p className="text-gray-600 mb-6 max-w-sm">
//                 We've searched thoroughly, but couldn't find relevant leads matching your current criteria.
//               </p>

//               <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-6 text-left w-full">
//                 <h3 className="font-medium text-amber-800 mb-2 flex items-center">
//                   <Sparkles size={18} className="mr-2 text-amber-500" />
//                   Suggestions to improve results
//                 </h3>
//                 <ul className="text-sm text-amber-700 space-y-2">
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5">•</div>
//                     <span>Try broadening your keywords to capture more conversations</span>
//                   </li>
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5">•</div>
//                     <span>Add industry-specific terms your potential customers might use</span>
//                   </li>
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5">•</div>
//                     <span>Include problem statements related to what your product solves</span>
//                   </li>
//                 </ul>
//               </div>

//               <div className="flex flex-col sm:flex-row justify-center gap-3 w-full">
//                 <button
//                   onClick={onEditKeywords}
//                   className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-800 transition-colors flex items-center justify-center"
//                 >
//                   <Edit size={16} className="mr-2" />
//                   Edit Keywords
//                 </button>
//                 <button
//                   onClick={onRetry}
//                   className={`px-4 py-2 ${platformBgColor} ${platformHoverBgColor} rounded-lg text-white transition-colors flex items-center justify-center`}
//                 >
//                   <RefreshCw size={16} className="mr-2" />
//                   Try Again
//                 </button>
//               </div>

//               <motion.p
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 1 }}
//                 transition={{ delay: 0.5 }}
//                 className="text-sm text-gray-500 mt-6 flex items-center justify-center"
//               >
//                 <CheckCircle size={14} className="mr-1 text-green-500" />
//                 No credits were consumed for this search
//               </motion.p>
//             </motion.div>
//           ) : isComplete ? (
//             // Success State (with results)
//             <motion.div
//               initial={{ scale: 0, opacity: 0 }}
//               animate={{ scale: 1, opacity: 1 }}
//               transition={{ type: "spring", damping: 20, stiffness: 300, delay: 0.1 }}
//             >
//               <div className={`w-24 h-24 rounded-full ${platformBgLightColor} flex items-center justify-center mx-auto mb-4`}>
//                 <Trophy className={`w-12 h-12 ${platformTextColor}`} />
//               </div>
//               <h2 className="text-2xl font-bold text-gray-800 mb-2">Analysis Complete!</h2>
//               <p className="text-gray-600 mb-4">
//                 We've found <span className="font-bold">{newPostsCount}</span> potential {newPostsCount === 1 ? "lead" : "leads"} for your
//                 business!
//               </p>

//               <div className={`mt-4 ${platformBgLightColor} rounded-lg p-4 border ${platformBorderColor}`}>
//                 <div className="flex items-center justify-between">
//                   <div className="flex items-center">
//                     <MessageSquare className={`mr-2 ${platformTextColor}`} size={18} />
//                     <span className="font-medium text-gray-700">Remaining credits:</span>
//                   </div>
//                   <span className="font-bold text-gray-800">{remainingCredits}</span>
//                 </div>
//               </div>

//               <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="mt-6">
//                 <button
//                   onClick={onClose}
//                   className={`px-5 py-2 ${platformBgColor} ${platformHoverBgColor} rounded-lg text-white transition-colors flex items-center mx-auto`}
//                 >
//                   <span>View Results</span>
//                   <ArrowRight size={16} className="ml-1" />
//                 </button>
//               </motion.div>
//             </motion.div>
//           ) : showError ? (
//             // Error State
//             <div>
//               <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
//                 <AlertCircle className="w-12 h-12 text-red-500" />
//               </div>
//               <h2 className="text-xl font-bold text-gray-800 mb-3">Taking longer than expected</h2>
//               <p className="text-gray-600 mb-6">The analysis is still running but taking longer than usual.</p>
//               <div className="flex justify-center space-x-4">
//                 <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800 transition-colors">
//                   Cancel
//                 </button>
//                 <button
//                   onClick={() => {
//                     setShowError(false);
//                     errorTimeoutRef.current = setTimeout(() => setShowError(true), 2 * 60 * 1000);
//                   }}
//                   className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
//                 >
//                   Continue waiting
//                 </button>
//               </div>
//             </div>
//           ) : (
//             // Active Progress State
//             <>
//               <div className="relative w-32 h-32 mx-auto mb-6">
//                 {/* Outer circular progress */}
//                 <svg className="w-full h-full" viewBox="0 0 100 100">
//                   <circle cx="50" cy="50" r="45" fill="none" stroke="#f0f0f0" strokeWidth="8" />
//                   <motion.circle
//                     cx="50"
//                     cy="50"
//                     r="45"
//                     fill="none"
//                     stroke={platform === "twitter" ? "#3b82f6" : "#10b981"}
//                     strokeWidth="8"
//                     strokeLinecap="round"
//                     initial={{ pathLength: 0 }}
//                     animate={{ pathLength: progress / 100 }}
//                     transition={{ duration: 0.5, ease: "easeInOut" }}
//                     style={{ rotate: -90, transformOrigin: "center" }}
//                   />
//                 </svg>

//                 {/* Inner content */}
//                 <div className="absolute inset-0 flex flex-col items-center justify-center">
//                   <div className="flex items-center mb-1">
//                     {platformIcon}
//                     <span className={`text-xl font-bold ${platformTextColor} ml-1`}>{Math.round(progress)}%</span>
//                   </div>
//                   <div className="flex space-x-1 mt-1">
//                     {[0, 0, 0].map((_, i) => (
//                       <motion.div
//                         key={i}
//                         animate={{ scale: [1, 1.2, 1] }}
//                         transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.2, delay: i * 0.3 }}
//                         className={`w-1.5 h-1.5 rounded-full ${platformBgColor}`}
//                       />
//                     ))}
//                   </div>
//                 </div>
//               </div>

//               <h2 className="text-xl font-bold text-gray-800 mb-1">Analyzing {platformName}</h2>
//               <p className="text-gray-500 text-sm mb-2">Finding potential customers for your product</p>
//               <p className="text-gray-400 text-xs flex items-center justify-center">
//                 <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//                   <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
//                   <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
//                 </svg>
//                 Estimated time: {Math.max(1, Math.ceil(3 * (1 - progress / 100)))} minute
//                 {Math.ceil(3 * (1 - progress / 100)) !== 1 ? "s" : ""} remaining
//               </p>
//             </>
//           )}
//         </div>

//         {/* Steps and tips section - only show during active analysis */}
//         {!isComplete && !showError && (
//           <>
//             {/* Analysis steps */}
//             <div className="mb-8">
//               {ANALYSIS_STEPS.map((step, idx) => (
//                 <div key={idx} className="flex items-center mb-3 last:mb-0">
//                   <div
//                     className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 transition-colors duration-300 ${
//                       idx < activeStep ? "bg-green-500" : idx === activeStep ? platformBgLightColor : "bg-gray-100"
//                     }`}
//                   >
//                     {idx < activeStep ? (
//                       <CheckCircle size={16} className="text-white" />
//                     ) : idx === activeStep ? (
//                       <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
//                         <Loader size={14} className={platformTextColor} />
//                       </motion.div>
//                     ) : (
//                       <div className="w-2 h-2 bg-gray-300 rounded-full" />
//                     )}
//                   </div>
//                   <div className="flex-1">
//                     <div className="flex justify-between items-center">
//                       <span
//                         className={`text-sm font-medium ${
//                           idx < activeStep ? "text-green-600" : idx === activeStep ? platformTextColor : "text-gray-400"
//                         }`}
//                       >
//                         {step.label}
//                       </span>
//                       {idx < activeStep && <span className="text-xs text-green-500">Done</span>}
//                     </div>
//                     {idx === activeStep && (
//                       <div className="mt-1 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
//                         <motion.div
//                           className={platformBgColor}
//                           initial={{ width: 0 }}
//                           animate={{
//                             width: `${Math.min(
//                               100,
//                               ((progress - (idx > 0 ? ANALYSIS_STEPS[idx - 1].completeAt : 0)) /
//                                 (step.completeAt - (idx > 0 ? ANALYSIS_STEPS[idx - 1].completeAt : 0))) *
//                                 100
//                             )}%`,
//                           }}
//                           transition={{ duration: 0.5 }}
//                         />
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               ))}
//             </div>

//             {/* Rotating tips */}
//             <AnimatePresence mode="wait">
//               <motion.div
//                 key={currentTipIndex}
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 transition={{ duration: 0.3 }}
//                 className={`p-4 ${platformBgLightColor} rounded-lg ${platformBorderColor} border`}
//               >
//                 <div className="flex items-start">
//                   <div className="mr-3 mt-1">{ANALYSIS_TIPS[currentTipIndex].icon}</div>
//                   <div>
//                     <h4 className={`font-medium ${platformTextColor} mb-1`}>{ANALYSIS_TIPS[currentTipIndex].title}</h4>
//                     <p className="text-gray-600 text-sm">{ANALYSIS_TIPS[currentTipIndex].text}</p>
//                   </div>
//                 </div>
//               </motion.div>
//             </AnimatePresence>
//           </>
//         )}
//       </motion.div>
//     </div>
//   );
// };

// export default React.memo(AnalysisProgressModal);

// const AnalysisProgressModal = ({ isOpen, progress, platform, newPostsCount, onClose, onEditKeywords }) => {
//   const [isComplete, setIsComplete] = useState(false);
//   const timeoutRef = useRef(null);

//   // Platform color
//   const color = platform === "twitter" ? "blue" : "green";

//   // Only auto-close if results were found, otherwise keep open for user action
//   useEffect(() => {
//     if (progress >= 100) {
//       setIsComplete(true);

//       // Only auto-close on success (when posts were found)
//       if (newPostsCount > 0) {
//         timeoutRef.current = setTimeout(onClose, 2000);
//       }
//       // Do NOT auto-close when no posts found
//     }

//     return () => {
//       if (timeoutRef.current) {
//         clearTimeout(timeoutRef.current);
//       }
//     };
//   }, [progress, newPostsCount, onClose]);

//   if (!isOpen) return null;

//   const hasResults = isComplete && newPostsCount > 0;
//   const noResults = isComplete && newPostsCount === 0;

//   return (
//     <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
//       <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl text-center">
//         {noResults ? (
//           <>
//             {/* No Results State - Stays open until user action */}
//             <div className="w-20 h-20 rounded-full bg-gray-50 mx-auto mb-6 flex items-center justify-center">
//               <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round">
//                 <circle cx="11" cy="11" r="8" />
//                 <path d="m21 21-4.3-4.3" />
//               </svg>
//             </div>

//             {/* <h2 className="text-2xl font-medium text-gray-900 mb-2">No Matches Found</h2> */}
//             {/* <h2 className="text-2xl font-medium text-gray-900 mb-2">You are up to date</h2>
//             <p className="text-gray-500 mb-8 max-w-xs mx-auto">We couldn't find leads matching your current keywords.</p> */}
//             <h2 className="text-2xl font-semibold text-gray-900 mb-2">You're All Caught Up</h2>
//             <p className="text-gray-500 mb-8 max-w-xs mx-auto">No more leads matched your current keywords.</p>

//             <div className="flex flex-col gap-3">
//               <button onClick={onEditKeywords} className={`w-full py-3 bg-${color}-600 text-white rounded-xl font-medium`}>
//                 Try Different Keywords
//               </button>
//               <button onClick={onClose} className="w-full py-3 bg-gray-100 text-gray-800 rounded-xl font-medium">
//                 Close
//               </button>
//             </div>

//             <p className="text-xs text-gray-400 mt-4">No credits were used for this search</p>
//           </>
//         ) : hasResults ? (
//           <>
//             {/* Results Found State - Will auto-close */}
//             <div className={`w-20 h-20 rounded-full bg-${color}-50 mx-auto mb-6 flex items-center justify-center`}>
//               <svg
//                 width="48"
//                 height="48"
//                 viewBox="0 0 24 24"
//                 fill="none"
//                 stroke={`var(--${color}-600)`}
//                 strokeWidth="1.5"
//                 strokeLinecap="round"
//               >
//                 <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
//                 <polyline points="22 4 12 14.01 9 11.01" />
//               </svg>
//             </div>

//             <h2 className="text-2xl font-medium text-gray-900 mb-2">Found {newPostsCount} Leads</h2>
//             <p className="text-gray-500 mb-8">Your results are ready to view.</p>

//             <button onClick={onClose} className={`w-full py-3 bg-${color}-600 text-white rounded-xl font-medium`}>
//               View Results
//             </button>
//           </>
//         ) : (
//           <>
//             {/* Progress State */}
//             <div className="relative w-20 h-20 mx-auto mb-6">
//               <svg className="w-full h-full" viewBox="0 0 100 100">
//                 <circle cx="50" cy="50" r="40" stroke="#f3f4f6" strokeWidth="8" fill="none" />
//                 <circle
//                   cx="50"
//                   cy="50"
//                   r="40"
//                   stroke={`var(--${color}-500)`}
//                   strokeWidth="8"
//                   fill="none"
//                   strokeLinecap="round"
//                   strokeDasharray="251.2"
//                   strokeDashoffset={251.2 - (251.2 * progress) / 100}
//                   transform="rotate(-90 50 50)"
//                 />
//               </svg>
//               <div className="absolute inset-0 flex items-center justify-center text-lg font-medium text-gray-800">
//                 {Math.round(progress)}%
//               </div>
//             </div>

//             <h2 className="text-2xl font-medium text-gray-900 mb-2">{platform === "twitter" ? "Searching Twitter" : "Searching Reddit"}</h2>
//             <p className="text-gray-500 mb-3">Finding leads for your product</p>
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// export default React.memo(AnalysisProgressModal);

// import React, { useState, useEffect, useRef } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { CheckCircle, Loader, Search, Filter, AlertCircle, Edit, RefreshCw, Sparkles, Twitter, Globe } from "lucide-react";

// // Tips that rotate during analysis to keep users engaged
// const ANALYSIS_TIPS = [
//   {
//     icon: <Search className="text-blue-500" />,
//     title: "Scanning conversations",
//     text: "We're analyzing thousands of online conversations to find potential customers.",
//   },
//   {
//     icon: <Filter className="text-purple-500" />,
//     title: "Filtering results",
//     text: "We're removing low-quality matches to ensure you only see relevant leads.",
//   },
//   {
//     icon: <Sparkles className="text-amber-500" />,
//     title: "Generating responses",
//     text: "Our AI is crafting personalized response suggestions for each lead.",
//   },
// ];

// // Analysis steps to show progress
// const ANALYSIS_STEPS = [
//   { label: "Collecting data", completeAt: 25 },
//   { label: "Processing conversations", completeAt: 50 },
//   { label: "Analyzing intent", completeAt: 75 },
//   { label: "Scoring and filtering", completeAt: 95 },
//   { label: "Finalizing results", completeAt: 100 },
// ];

// const AnalysisProgressModal = ({ isOpen, progress, platform = "reddit", newPostsCount, onClose, onRetry, onEditKeywords }) => {
//   const [currentTipIndex, setCurrentTipIndex] = useState(0);
//   const [isComplete, setIsComplete] = useState(false);
//   const timeoutRef = useRef(null);

//   // Platform-specific styling
//   const isPlatformTwitter = platform === "twitter";
//   const platformColor = isPlatformTwitter ? "blue" : "green";
//   const platformIcon = isPlatformTwitter ? <Twitter size={24} /> : <Globe size={24} />;
//   const platformName = isPlatformTwitter ? "Twitter" : "Reddit";

//   // Rotate tips every 6 seconds during analysis
//   useEffect(() => {
//     if (progress < 100 && isOpen) {
//       const interval = setInterval(() => {
//         setCurrentTipIndex((prev) => (prev + 1) % ANALYSIS_TIPS.length);
//       }, 6000);
//       return () => clearInterval(interval);
//     }
//   }, [progress, isOpen]);

//   // Handle completion state
//   useEffect(() => {
//     if (progress >= 100) {
//       setIsComplete(true);

//       // Only auto-close on success (when posts were found)
//       if (newPostsCount > 0) {
//         timeoutRef.current = setTimeout(onClose, 2500);
//       }
//       // Do NOT auto-close when no posts found to allow user action
//     }

//     return () => {
//       if (timeoutRef.current) {
//         clearTimeout(timeoutRef.current);
//       }
//     };
//   }, [progress, newPostsCount, onClose]);

//   // Find the current step
//   const currentStep = ANALYSIS_STEPS.findIndex((step) => progress < step.completeAt);
//   const activeStep = currentStep === -1 ? ANALYSIS_STEPS.length - 1 : currentStep;

//   if (!isOpen) return null;

//   // Different states to render
//   const hasResults = isComplete && newPostsCount > 0;
//   const noResults = isComplete && newPostsCount === 0;
//   const inProgress = !isComplete;

//   return (
//     <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
//       <motion.div
//         initial={{ opacity: 0, scale: 0.95, y: 10 }}
//         animate={{ opacity: 1, scale: 1, y: 0 }}
//         exit={{ opacity: 0, scale: 0.95, y: 10 }}
//         transition={{ type: "spring", damping: 30, stiffness: 400 }}
//         className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl"
//       >
//         {/* ===== NO RESULTS STATE ===== */}
//         {noResults && (
//           <div className="text-center">
//             <div className="w-20 h-20 rounded-full bg-amber-50 mx-auto mb-4 flex items-center justify-center">
//               <Search className="w-10 h-10 text-amber-500" />
//             </div>

//             <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Matches Found</h2>
//             <p className="text-gray-600 mb-6 max-w-sm mx-auto">
//               We couldn't find leads matching your current keywords. Try adjusting your search criteria.
//             </p>

//             <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-6 text-left">
//               <h3 className="font-medium text-amber-800 mb-2 flex items-center">
//                 <Sparkles size={18} className="mr-2 text-amber-500" />
//                 Suggestions to improve results
//               </h3>
//               <ul className="text-sm text-amber-700 space-y-2">
//                 <li className="flex items-start">
//                   <div className="mr-2 mt-0.5">•</div>
//                   <span>Try broader keywords to capture more conversations</span>
//                 </li>
//                 <li className="flex items-start">
//                   <div className="mr-2 mt-0.5">•</div>
//                   <span>Add industry-specific terms your customers might use</span>
//                 </li>
//                 <li className="flex items-start">
//                   <div className="mr-2 mt-0.5">•</div>
//                   <span>Include problem statements your product solves</span>
//                 </li>
//               </ul>
//             </div>

//             <div className="flex flex-col sm:flex-row gap-3 justify-center">
//               <button
//                 onClick={onEditKeywords}
//                 className={`px-4 py-3 flex-1 bg-${platformColor}-600 text-white rounded-lg font-medium hover:bg-${platformColor}-700 transition-colors flex items-center justify-center`}
//               >
//                 <Edit size={16} className="mr-2" />
//                 Edit Keywords
//               </button>
//               <button
//                 onClick={onRetry}
//                 className="px-4 py-3 flex-1 bg-gray-100 text-gray-800 rounded-lg font-medium hover:bg-gray-200 transition-colors"
//               >
//                 Try Again
//               </button>
//             </div>

//             <p className="text-xs text-gray-500 mt-4">No credits were used for this search</p>
//           </div>
//         )}

//         {/* ===== SUCCESS STATE ===== */}
//         {hasResults && (
//           <div className="text-center">
//             <div className={`w-20 h-20 rounded-full bg-${platformColor}-50 mx-auto mb-4 flex items-center justify-center`}>
//               <CheckCircle className={`w-10 h-10 text-${platformColor}-500`} />
//             </div>

//             <h2 className="text-2xl font-semibold text-gray-900 mb-2">Success!</h2>
//             <p className="text-gray-600 mb-6">
//               Found <span className="font-bold">{newPostsCount}</span> potential {newPostsCount === 1 ? "lead" : "leads"} for your business!
//             </p>

//             <button
//               onClick={onClose}
//               className={`w-full py-3 bg-${platformColor}-600 text-white rounded-lg font-medium hover:bg-${platformColor}-700 transition-colors flex items-center justify-center`}
//             >
//               View Results
//               <svg className="ml-2 w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//                 <path
//                   d="M5 12H19M19 12L13 6M19 12L13 18"
//                   stroke="currentColor"
//                   strokeWidth="2"
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                 />
//               </svg>
//             </button>
//           </div>
//         )}

//         {/* ===== PROGRESS STATE ===== */}
//         {inProgress && (
//           <>
//             <div className="text-center mb-6">
//               <div className="relative w-24 h-24 mx-auto mb-4">
//                 {/* Circle progress */}
//                 <svg className="w-full h-full" viewBox="0 0 100 100">
//                   <circle cx="50" cy="50" r="42" fill="none" stroke="#f0f0f0" strokeWidth="8" />
//                   <motion.circle
//                     cx="50"
//                     cy="50"
//                     r="42"
//                     fill="none"
//                     stroke={isPlatformTwitter ? "#3b82f6" : "#10b981"}
//                     strokeWidth="8"
//                     strokeLinecap="round"
//                     initial={{ pathLength: 0 }}
//                     animate={{ pathLength: progress / 100 }}
//                     transition={{ duration: 0.5, ease: "easeInOut" }}
//                     style={{ rotate: -90, transformOrigin: "center" }}
//                   />
//                 </svg>

//                 {/* Inner content */}
//                 <div className="absolute inset-0 flex flex-col items-center justify-center">
//                   <div className="flex items-center">
//                     {platformIcon}
//                     <span className={`text-xl font-bold text-${platformColor}-600 ml-1`}>{Math.round(progress)}%</span>
//                   </div>
//                 </div>
//               </div>

//               <h2 className="text-xl font-bold text-gray-800 mb-2">Analyzing {platformName}</h2>
//               <p className="text-gray-500 text-sm">Finding potential customers for your product</p>
//               <p className="text-gray-400 text-xs flex items-center justify-center mt-1">
//                 <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//                   <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
//                   <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
//                 </svg>
//                 Estimated time: {Math.max(1, Math.ceil(2 * (1 - progress / 100)))}{" "}
//                 {Math.ceil(2 * (1 - progress / 100)) === 1 ? "minute" : "minutes"} remaining
//               </p>
//             </div>

//             {/* Analysis steps */}
//             <div className="mb-6">
//               {ANALYSIS_STEPS.map((step, index) => (
//                 <div key={index} className="flex items-center mb-3 last:mb-0">
//                   <div
//                     className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 transition-colors duration-300
//                       ${index < activeStep ? "bg-green-500" : index === activeStep ? `bg-${platformColor}-100` : "bg-gray-100"}`}
//                   >
//                     {index < activeStep ? (
//                       <CheckCircle size={16} className="text-white" />
//                     ) : index === activeStep ? (
//                       <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
//                         <Loader size={14} className={`text-${platformColor}-500`} />
//                       </motion.div>
//                     ) : (
//                       <div className="w-2 h-2 bg-gray-300 rounded-full" />
//                     )}
//                   </div>

//                   <div className="flex-1">
//                     <div className="flex justify-between items-center">
//                       <span
//                         className={`text-sm font-medium
//                           ${index < activeStep ? "text-green-600" : index === activeStep ? `text-${platformColor}-600` : "text-gray-400"}
//                         `}
//                       >
//                         {step.label}
//                       </span>
//                       {index < activeStep && <span className="text-xs text-green-500">Done</span>}
//                     </div>

//                     {/* Progress bar for current step */}
//                     {index === activeStep && (
//                       <div className="mt-1 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
//                         <motion.div
//                           className={`bg-${platformColor}-500`}
//                           initial={{ width: 0 }}
//                           animate={{
//                             width: `${Math.min(
//                               100,
//                               ((progress - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0)) /
//                                 (step.completeAt - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0))) *
//                                 100
//                             )}%`,
//                           }}
//                           transition={{ duration: 0.5 }}
//                         />
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               ))}
//             </div>

//             {/* Rotating tips */}
//             <AnimatePresence mode="wait">
//               <motion.div
//                 key={currentTipIndex}
//                 initial={{ opacity: 0, y: 10 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -10 }}
//                 transition={{ duration: 0.3 }}
//                 className={`p-4 bg-${platformColor}-50 rounded-lg border border-${platformColor}-100`}
//               >
//                 <div className="flex items-start">
//                   <div className="mr-3 mt-1">{ANALYSIS_TIPS[currentTipIndex].icon}</div>
//                   <div>
//                     <h4 className={`font-medium text-${platformColor}-800 mb-1`}>{ANALYSIS_TIPS[currentTipIndex].title}</h4>
//                     <p className="text-gray-600 text-sm">{ANALYSIS_TIPS[currentTipIndex].text}</p>
//                   </div>
//                 </div>
//               </motion.div>
//             </AnimatePresence>
//           </>
//         )}
//       </motion.div>
//     </div>
//   );
// };

// export default React.memo(AnalysisProgressModal);

// import React, { useState, useEffect, useRef } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import {
//   CheckCircle,
//   Loader,
//   Search,
//   Filter,
//   AlertCircle,
//   Edit,
//   RefreshCw,
//   Sparkles,
//   Twitter,
//   Globe,
//   Clock,
//   ArrowRight,
//   MessageSquare,
//   BarChart,
//   Trophy,
// } from "lucide-react";

// // Tips that rotate during analysis to keep users engaged
// const ANALYSIS_TIPS = [
//   {
//     icon: <Search className="w-6 h-6" />,
//     title: "Scanning conversations",
//     text: "We're analyzing thousands of online conversations to find potential customers.",
//   },
//   {
//     icon: <Filter className="w-6 h-6" />,
//     title: "Filtering results",
//     text: "We're removing low-quality matches to ensure you only see relevant leads.",
//   },
//   {
//     icon: <MessageSquare className="w-6 h-6" />,
//     title: "Processing text",
//     text: "Our AI is analyzing context and intent in each conversation.",
//   },
//   {
//     icon: <BarChart className="w-6 h-6" />,
//     title: "Scoring leads",
//     text: "Each potential lead is being scored based on relevance to your product.",
//   },
//   {
//     icon: <Sparkles className="w-6 h-6" />,
//     title: "Generating responses",
//     text: "Our AI is crafting personalized response suggestions for each lead.",
//   },
// ];

// // Analysis steps to show progress
// const ANALYSIS_STEPS = [
//   { label: "Collecting data", completeAt: 20 },
//   { label: "Processing conversations", completeAt: 40 },
//   { label: "Analyzing intent", completeAt: 65 },
//   { label: "Scoring and filtering", completeAt: 85 },
//   { label: "Generating responses", completeAt: 95 },
//   { label: "Finalizing results", completeAt: 100 },
// ];

// const AnalysisProgressModal = ({ isOpen, progress, platform = "reddit", newPostsCount, onClose, onRetry, onEditKeywords }) => {
//   const [currentTipIndex, setCurrentTipIndex] = useState(0);
//   const [isComplete, setIsComplete] = useState(false);
//   const [remainingTime, setRemainingTime] = useState(180); // 3 minutes in seconds
//   const timeoutRef = useRef(null);
//   const intervalRef = useRef(null);

//   // Platform-specific styling
//   const isPlatformTwitter = platform === "twitter";
//   const platformColors = {
//     primary: isPlatformTwitter ? "#1DA1F2" : "#0F9D58", // Twitter blue vs Reddit green
//     secondary: isPlatformTwitter ? "#1A91DA" : "#0E8C4F",
//     light: isPlatformTwitter ? "#E8F5FD" : "#E6F4EA",
//     medium: isPlatformTwitter ? "#A8D4F5" : "#A6DDBE",
//     dark: isPlatformTwitter ? "#0C7ABF" : "#0B7A45",
//     gradient: isPlatformTwitter ? "linear-gradient(135deg, #1DA1F2, #0C7ABF)" : "linear-gradient(135deg, #0F9D58, #0B7A45)",
//     gradientLight: isPlatformTwitter ? "linear-gradient(135deg, #E8F5FD, #A8D4F5)" : "linear-gradient(135deg, #E6F4EA, #A6DDBE)",
//   };

//   const platformIcon = isPlatformTwitter ? (
//     <Twitter size={28} style={{ color: platformColors.primary }} />
//   ) : (
//     <Globe size={28} style={{ color: platformColors.primary }} />
//   );

//   const platformName = isPlatformTwitter ? "Twitter" : "Reddit";

//   // Rotate tips every 6 seconds during analysis
//   useEffect(() => {
//     if (progress < 100 && isOpen) {
//       const interval = setInterval(() => {
//         setCurrentTipIndex((prev) => (prev + 1) % ANALYSIS_TIPS.length);
//       }, 6000);
//       return () => clearInterval(interval);
//     }
//   }, [progress, isOpen]);

//   // Handle completion state
//   useEffect(() => {
//     if (progress >= 100) {
//       setIsComplete(true);

//       // Only auto-close on success (when posts were found)
//       if (newPostsCount > 0) {
//         timeoutRef.current = setTimeout(onClose, 2500);
//       }
//       // Do NOT auto-close when no posts found to allow user action
//     }

//     return () => {
//       if (timeoutRef.current) {
//         clearTimeout(timeoutRef.current);
//       }
//     };
//   }, [progress, newPostsCount, onClose]);

//   // Countdown timer effect
//   useEffect(() => {
//     if (progress < 100 && isOpen) {
//       // Set initial countdown based on progress
//       setRemainingTime(Math.max(30, Math.ceil(180 * (1 - progress / 100))));

//       intervalRef.current = setInterval(() => {
//         setRemainingTime((prev) => {
//           if (prev <= 1) {
//             clearInterval(intervalRef.current);
//             return 0;
//           }
//           return prev - 1;
//         });
//       }, 1000);

//       return () => {
//         if (intervalRef.current) {
//           clearInterval(intervalRef.current);
//         }
//       };
//     }
//   }, [progress, isOpen]);

//   // Format remaining time as MM:SS
//   const formatTime = (seconds) => {
//     const mins = Math.floor(seconds / 60);
//     const secs = seconds % 60;
//     return `${mins}:${secs.toString().padStart(2, "0")}`;
//   };

//   // Find the current step
//   const currentStep = ANALYSIS_STEPS.findIndex((step) => progress < step.completeAt);
//   const activeStep = currentStep === -1 ? ANALYSIS_STEPS.length - 1 : currentStep;

//   if (!isOpen) return null;

//   // Different states to render
//   const hasResults = isComplete && newPostsCount > 0;
//   const noResults = isComplete && newPostsCount === 0;
//   const inProgress = !isComplete;

//   return (
//     <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
//       <motion.div
//         initial={{ opacity: 0, scale: 0.95, y: 10 }}
//         animate={{ opacity: 1, scale: 1, y: 0 }}
//         exit={{ opacity: 0, scale: 0.95, y: 10 }}
//         transition={{ type: "spring", damping: 30, stiffness: 400 }}
//         style={{
//           boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
//           maxWidth: "550px",
//           width: "100%",
//         }}
//         className="bg-white rounded-2xl overflow-hidden"
//       >
//         {/* Platform-specific colored header bar */}
//         <div
//           style={{
//             background: platformColors.gradient,
//             height: "8px",
//           }}
//         />

//         <div className="p-8">
//           {/* ===== NO RESULTS STATE ===== */}
//           {noResults && (
//             <div className="text-center">
//               <div
//                 style={{
//                   background: platformColors.light,
//                   borderColor: platformColors.medium,
//                 }}
//                 className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center border-2"
//               >
//                 <Search style={{ color: platformColors.primary }} className="w-12 h-12" />
//               </div>

//               <h2 className="text-2xl font-bold text-gray-900 mb-3">No Matches Found</h2>
//               <p className="text-gray-600 mb-6 max-w-sm mx-auto">
//                 We couldn't find leads matching your current keywords on {platformName}. Try adjusting your search criteria.
//               </p>

//               <div
//                 style={{ background: platformColors.light, borderColor: platformColors.medium }}
//                 className="border rounded-xl p-5 mb-6 text-left"
//               >
//                 <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
//                   <Sparkles style={{ color: platformColors.primary }} className="w-5 h-5 mr-2" />
//                   Suggestions to improve results
//                 </h3>
//                 <ul className="text-sm text-gray-700 space-y-3">
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5 text-lg" style={{ color: platformColors.primary }}>
//                       •
//                     </div>
//                     <span>Try broader keywords to capture more conversations</span>
//                   </li>
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5 text-lg" style={{ color: platformColors.primary }}>
//                       •
//                     </div>
//                     <span>Add industry-specific terms your customers might use</span>
//                   </li>
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5 text-lg" style={{ color: platformColors.primary }}>
//                       •
//                     </div>
//                     <span>Include problem statements your product solves</span>
//                   </li>
//                 </ul>
//               </div>

//               <div className="flex flex-col sm:flex-row gap-3 justify-center">
//                 <button
//                   onClick={onEditKeywords}
//                   style={{
//                     background: platformColors.gradient,
//                     boxShadow: `0 2px 8px ${isPlatformTwitter ? "rgba(29, 161, 242, 0.3)" : "rgba(15, 157, 88, 0.3)"}`,
//                   }}
//                   className="px-5 py-3 flex-1 text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg flex items-center justify-center"
//                 >
//                   <Edit size={18} className="mr-2" />
//                   Edit Keywords
//                 </button>
//                 <button
//                   onClick={onRetry}
//                   className="px-5 py-3 flex-1 bg-gray-100 text-gray-800 rounded-xl font-medium hover:bg-gray-200 transition-colors"
//                 >
//                   <RefreshCw size={18} className="mr-2 inline-block" />
//                   Try Again
//                 </button>
//               </div>

//               <p className="text-xs text-gray-500 mt-5">No credits were used for this search</p>
//             </div>
//           )}

//           {/* ===== SUCCESS STATE ===== */}
//           {hasResults && (
//             <div className="text-center">
//               <motion.div
//                 initial={{ scale: 0.8, opacity: 0 }}
//                 animate={{ scale: 1, opacity: 1 }}
//                 transition={{
//                   type: "spring",
//                   stiffness: 300,
//                   damping: 20,
//                   delay: 0.1,
//                 }}
//                 style={{
//                   background: platformColors.light,
//                   borderColor: platformColors.medium,
//                 }}
//                 className="w-28 h-28 rounded-full mx-auto mb-6 flex items-center justify-center border-2"
//               >
//                 <Trophy style={{ color: platformColors.primary }} className="w-14 h-14" />
//               </motion.div>

//               <h2 className="text-2xl font-bold text-gray-900 mb-3">Success!</h2>
//               <p className="text-gray-600 mb-8 text-lg">
//                 Found{" "}
//                 <span className="font-bold" style={{ color: platformColors.primary }}>
//                   {newPostsCount}
//                 </span>{" "}
//                 potential {newPostsCount === 1 ? "lead" : "leads"} on {platformName}!
//               </p>

//               <motion.button
//                 whileHover={{ scale: 1.03 }}
//                 whileTap={{ scale: 0.98 }}
//                 onClick={onClose}
//                 style={{
//                   background: platformColors.gradient,
//                   boxShadow: `0 2px 8px ${isPlatformTwitter ? "rgba(29, 161, 242, 0.3)" : "rgba(15, 157, 88, 0.3)"}`,
//                 }}
//                 className="w-full py-3.5 text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg flex items-center justify-center text-lg"
//               >
//                 View Results
//                 <ArrowRight className="ml-2 w-5 h-5" />
//               </motion.button>
//             </div>
//           )}

//           {/* ===== PROGRESS STATE ===== */}
//           {inProgress && (
//             <>
//               <div className="text-center mb-8">
//                 <div className="relative w-32 h-32 mx-auto mb-6">
//                   {/* Background spinner - constant slow rotation */}
//                   <motion.div
//                     animate={{ rotate: 360 }}
//                     transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
//                     style={{
//                       width: "100%",
//                       height: "100%",
//                       position: "absolute",
//                       borderRadius: "100%",
//                       border: `4px dashed ${platformColors.medium}`,
//                       opacity: 0.5,
//                     }}
//                   />

//                   {/* Circle progress */}
//                   <svg className="w-full h-full" viewBox="0 0 100 100">
//                     <circle cx="50" cy="50" r="42" fill="none" stroke="#f0f0f0" strokeWidth="8" />
//                     <motion.circle
//                       cx="50"
//                       cy="50"
//                       r="42"
//                       fill="none"
//                       stroke={platformColors.primary}
//                       strokeWidth="8"
//                       strokeLinecap="round"
//                       initial={{ pathLength: 0 }}
//                       animate={{ pathLength: progress / 100 }}
//                       transition={{ duration: 0.5, ease: "easeInOut" }}
//                       style={{ rotate: -90, transformOrigin: "center" }}
//                     />
//                   </svg>

//                   {/* Inner content */}
//                   <div className="absolute inset-0 flex flex-col items-center justify-center">
//                     <div className="flex items-center mb-1">{platformIcon}</div>
//                     <span className="text-2xl font-bold" style={{ color: platformColors.primary }}>
//                       {Math.round(progress)}%
//                     </span>
//                   </div>
//                 </div>

//                 <h2 className="text-2xl font-bold text-gray-800 mb-2">Analyzing {platformName}</h2>
//                 <p className="text-gray-600 text-lg mb-2">Finding potential customers for your product</p>

//                 {/* Time remaining with animated clock */}
//                 <div className="flex items-center justify-center text-gray-500 mt-2">
//                   <Clock className="w-4 h-4 mr-2" />
//                   <motion.div
//                     key={remainingTime}
//                     initial={{ opacity: 0, y: 5 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     className="flex items-center"
//                   >
//                     <span className="text-sm">Estimated time remaining: </span>
//                     <span className="text-sm font-medium ml-1" style={{ color: platformColors.primary }}>
//                       {formatTime(remainingTime)}
//                     </span>
//                   </motion.div>
//                 </div>
//               </div>

//               {/* Analysis steps */}
//               <div className="mb-8">
//                 {ANALYSIS_STEPS.map((step, index) => (
//                   <div key={index} className="flex items-center mb-4 last:mb-0">
//                     <div
//                       style={{
//                         background: index < activeStep ? platformColors.primary : index === activeStep ? platformColors.light : "#f3f4f6",
//                         borderColor: index < activeStep || index === activeStep ? platformColors.medium : "#e5e7eb",
//                       }}
//                       className="w-8 h-8 rounded-full flex items-center justify-center mr-3 transition-colors duration-300 border"
//                     >
//                       {index < activeStep ? (
//                         <CheckCircle size={16} className="text-white" />
//                       ) : index === activeStep ? (
//                         <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
//                           <Loader size={16} style={{ color: platformColors.primary }} />
//                         </motion.div>
//                       ) : (
//                         <div className="w-2.5 h-2.5 bg-gray-300 rounded-full" />
//                       )}
//                     </div>

//                     <div className="flex-1">
//                       <div className="flex justify-between items-center">
//                         <span
//                           style={{
//                             color:
//                               index < activeStep ? platformColors.primary : index === activeStep ? platformColors.secondary : "#9ca3af",
//                           }}
//                           className="text-base font-medium"
//                         >
//                           {step.label}
//                         </span>
//                         {index < activeStep && (
//                           <span style={{ color: platformColors.primary }} className="text-xs font-medium">
//                             Complete
//                           </span>
//                         )}
//                       </div>

//                       {/* Progress bar for current step */}
//                       {index === activeStep && (
//                         <div className="mt-1.5 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
//                           <motion.div
//                             style={{ background: platformColors.gradient }}
//                             initial={{ width: 0 }}
//                             animate={{
//                               width: `${Math.min(
//                                 100,
//                                 ((progress - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0)) /
//                                   (step.completeAt - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0))) *
//                                   100
//                               )}%`,
//                             }}
//                             transition={{ duration: 0.5 }}
//                             className="h-full"
//                           />
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 ))}
//               </div>

//               {/* Rotating tips */}
//               <AnimatePresence mode="wait">
//                 <motion.div
//                   key={currentTipIndex}
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -10 }}
//                   transition={{ duration: 0.3 }}
//                   style={{
//                     background: platformColors.light,
//                     borderColor: platformColors.medium,
//                   }}
//                   className="p-5 rounded-xl border"
//                 >
//                   <div className="flex items-start">
//                     <div style={{ color: platformColors.primary }} className="mr-4 mt-1 flex-shrink-0">
//                       {ANALYSIS_TIPS[currentTipIndex].icon}
//                     </div>
//                     <div>
//                       <h4 style={{ color: platformColors.primary }} className="font-semibold text-lg mb-1">
//                         {ANALYSIS_TIPS[currentTipIndex].title}
//                       </h4>
//                       <p className="text-gray-700">{ANALYSIS_TIPS[currentTipIndex].text}</p>
//                     </div>
//                   </div>
//                 </motion.div>
//               </AnimatePresence>
//             </>
//           )}
//         </div>
//       </motion.div>
//     </div>
//   );
// };

// export default React.memo(AnalysisProgressModal);

// import React, { useState, useEffect, useRef } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import {
//   CheckCircle,
//   Loader,
//   Search,
//   Filter,
//   AlertCircle,
//   Edit,
//   RefreshCw,
//   Sparkles,
//   Twitter,
//   Globe,
//   Clock,
//   ArrowRight,
//   MessageSquare,
//   BarChart,
//   Trophy,
//   Briefcase,
// } from "lucide-react";

// // Tips that rotate during analysis to keep users engaged
// const ANALYSIS_TIPS = [
//   {
//     icon: <Search className="w-6 h-6" />,
//     title: "Scanning conversations",
//     text: "We're analyzing thousands of online conversations to find potential customers.",
//   },
//   {
//     icon: <Filter className="w-6 h-6" />,
//     title: "Filtering results",
//     text: "We're removing low-quality matches to ensure you only see relevant leads.",
//   },
//   {
//     icon: <MessageSquare className="w-6 h-6" />,
//     title: "Processing text",
//     text: "Our AI is analyzing context and intent in each conversation.",
//   },
//   {
//     icon: <BarChart className="w-6 h-6" />,
//     title: "Scoring leads",
//     text: "Each potential lead is being scored based on relevance to your product.",
//   },
//   {
//     icon: <Sparkles className="w-6 h-6" />,
//     title: "Generating responses",
//     text: "Our AI is crafting personalized response suggestions for each lead.",
//   },
// ];

// // Analysis steps to show progress
// const ANALYSIS_STEPS = [
//   { label: "Collecting data", completeAt: 20 },
//   { label: "Processing conversations", completeAt: 40 },
//   { label: "Analyzing intent", completeAt: 65 },
//   { label: "Scoring and filtering", completeAt: 85 },
//   { label: "Generating responses", completeAt: 95 },
//   { label: "Finalizing results", completeAt: 100 },
// ];

// const AnalysisProgressModal = ({ isOpen, progress, platform = "reddit", newPostsCount, onClose, onRetry, onEditKeywords }) => {
//   const [currentTipIndex, setCurrentTipIndex] = useState(0);
//   const [isComplete, setIsComplete] = useState(false);
//   const [remainingTime, setRemainingTime] = useState(180); // 3 minutes in seconds
//   const timeoutRef = useRef(null);
//   const intervalRef = useRef(null);

//   // Platform-specific styling
//   const getPlatformStyles = () => {
//     switch (platform) {
//       case "twitter":
//         return {
//           primary: "#1DA1F2",
//           secondary: "#1A91DA",
//           light: "#E8F5FD",
//           medium: "#A8D4F5",
//           dark: "#0C7ABF",
//           gradient: "linear-gradient(135deg, #1DA1F2, #0C7ABF)",
//           gradientLight: "linear-gradient(135deg, #E8F5FD, #A8D4F5)",
//           icon: <Twitter size={28} style={{ color: "#1DA1F2" }} />,
//           name: "Twitter",
//         };
//       case "linkedin":
//         return {
//           primary: "#0A66C2",
//           secondary: "#0077B5",
//           light: "#E8F3FD",
//           medium: "#A1C9E8",
//           dark: "#004182",
//           gradient: "linear-gradient(135deg, #0A66C2, #004182)",
//           gradientLight: "linear-gradient(135deg, #E8F3FD, #A1C9E8)",
//           icon: <Briefcase size={28} style={{ color: "#0A66C2" }} />,
//           name: "LinkedIn",
//         };
//       default: // reddit or any other
//         return {
//           primary: "#0F9D58",
//           secondary: "#0E8C4F",
//           light: "#E6F4EA",
//           medium: "#A6DDBE",
//           dark: "#0B7A45",
//           gradient: "linear-gradient(135deg, #0F9D58, #0B7A45)",
//           gradientLight: "linear-gradient(135deg, #E6F4EA, #A6DDBE)",
//           icon: <Globe size={28} style={{ color: "#0F9D58" }} />,
//           name: "Reddit",
//         };
//     }
//   };

//   const platformColors = getPlatformStyles();
//   const platformIcon = platformColors.icon;
//   const platformName = platformColors.name;

//   // Rotate tips every 6 seconds during analysis
//   useEffect(() => {
//     if (progress < 100 && isOpen) {
//       const interval = setInterval(() => {
//         setCurrentTipIndex((prev) => (prev + 1) % ANALYSIS_TIPS.length);
//       }, 6000);
//       return () => clearInterval(interval);
//     }
//   }, [progress, isOpen]);

//   // Handle completion state
//   useEffect(() => {
//     if (progress >= 100) {
//       setIsComplete(true);

//       // Only auto-close on success (when posts were found)
//       if (newPostsCount > 0) {
//         timeoutRef.current = setTimeout(onClose, 2500);
//       }
//       // Do NOT auto-close when no posts found to allow user action
//     }

//     return () => {
//       if (timeoutRef.current) {
//         clearTimeout(timeoutRef.current);
//       }
//     };
//   }, [progress, newPostsCount, onClose]);

//   // Countdown timer effect
//   useEffect(() => {
//     if (progress < 100 && isOpen) {
//       // Set initial countdown based on progress
//       setRemainingTime(Math.max(30, Math.ceil(180 * (1 - progress / 100))));

//       intervalRef.current = setInterval(() => {
//         setRemainingTime((prev) => {
//           if (prev <= 1) {
//             clearInterval(intervalRef.current);
//             return 0;
//           }
//           return prev - 1;
//         });
//       }, 1000);

//       return () => {
//         if (intervalRef.current) {
//           clearInterval(intervalRef.current);
//         }
//       };
//     }
//   }, [progress, isOpen]);

//   // Format remaining time as MM:SS
//   const formatTime = (seconds) => {
//     const mins = Math.floor(seconds / 60);
//     const secs = seconds % 60;
//     return `${mins}:${secs.toString().padStart(2, "0")}`;
//   };

//   // Find the current step
//   const currentStep = ANALYSIS_STEPS.findIndex((step) => progress < step.completeAt);
//   const activeStep = currentStep === -1 ? ANALYSIS_STEPS.length - 1 : currentStep;

//   if (!isOpen) return null;

//   // Different states to render
//   const hasResults = isComplete && newPostsCount > 0;
//   const noResults = isComplete && newPostsCount === 0;
//   const inProgress = !isComplete;

//   return (
//     <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
//       <motion.div
//         initial={{ opacity: 0, scale: 0.95, y: 10 }}
//         animate={{ opacity: 1, scale: 1, y: 0 }}
//         exit={{ opacity: 0, scale: 0.95, y: 10 }}
//         transition={{ type: "spring", damping: 30, stiffness: 400 }}
//         style={{
//           boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
//           maxWidth: "550px",
//           width: "100%",
//         }}
//         className="bg-white rounded-2xl overflow-hidden"
//       >
//         {/* Platform-specific colored header bar */}
//         <div
//           style={{
//             background: platformColors.gradient,
//             height: "8px",
//           }}
//         />

//         <div className="p-8">
//           {/* ===== NO RESULTS STATE ===== */}
//           {noResults && (
//             <div className="text-center">
//               <div
//                 style={{
//                   background: platformColors.light,
//                   borderColor: platformColors.medium,
//                 }}
//                 className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center border-2"
//               >
//                 <Search style={{ color: platformColors.primary }} className="w-12 h-12" />
//               </div>

//               <h2 className="text-2xl font-bold text-gray-900 mb-3">No Matches Found</h2>
//               <p className="text-gray-600 mb-6 max-w-sm mx-auto">
//                 We couldn't find leads matching your current keywords on {platformName}. Try adjusting your search criteria.
//               </p>

//               <div
//                 style={{ background: platformColors.light, borderColor: platformColors.medium }}
//                 className="border rounded-xl p-5 mb-6 text-left"
//               >
//                 <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
//                   <Sparkles style={{ color: platformColors.primary }} className="w-5 h-5 mr-2" />
//                   Suggestions to improve results
//                 </h3>
//                 <ul className="text-sm text-gray-700 space-y-3">
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5 text-lg" style={{ color: platformColors.primary }}>
//                       •
//                     </div>
//                     <span>Try broader keywords to capture more conversations</span>
//                   </li>
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5 text-lg" style={{ color: platformColors.primary }}>
//                       •
//                     </div>
//                     <span>Add industry-specific terms your customers might use</span>
//                   </li>
//                   <li className="flex items-start">
//                     <div className="mr-2 mt-0.5 text-lg" style={{ color: platformColors.primary }}>
//                       •
//                     </div>
//                     <span>Include problem statements your product solves</span>
//                   </li>
//                 </ul>
//               </div>

//               <div className="flex flex-col sm:flex-row gap-3 justify-center">
//                 <button
//                   onClick={onEditKeywords}
//                   style={{
//                     background: platformColors.gradient,
//                     boxShadow: `0 2px 8px rgba(0, 0, 0, 0.3)`,
//                   }}
//                   className="px-5 py-3 flex-1 text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg flex items-center justify-center"
//                 >
//                   <Edit size={18} className="mr-2" />
//                   Edit Keywords
//                 </button>
//                 <button
//                   onClick={onRetry}
//                   className="px-5 py-3 flex-1 bg-gray-100 text-gray-800 rounded-xl font-medium hover:bg-gray-200 transition-colors"
//                 >
//                   <RefreshCw size={18} className="mr-2 inline-block" />
//                   Try Again
//                 </button>
//               </div>

//               <p className="text-xs text-gray-500 mt-5">No credits were used for this search</p>
//             </div>
//           )}

//           {/* ===== SUCCESS STATE ===== */}
//           {hasResults && (
//             <div className="text-center">
//               <motion.div
//                 initial={{ scale: 0.8, opacity: 0 }}
//                 animate={{ scale: 1, opacity: 1 }}
//                 transition={{
//                   type: "spring",
//                   stiffness: 300,
//                   damping: 20,
//                   delay: 0.1,
//                 }}
//                 style={{
//                   background: platformColors.light,
//                   borderColor: platformColors.medium,
//                 }}
//                 className="w-28 h-28 rounded-full mx-auto mb-6 flex items-center justify-center border-2"
//               >
//                 <Trophy style={{ color: platformColors.primary }} className="w-14 h-14" />
//               </motion.div>

//               <h2 className="text-2xl font-bold text-gray-900 mb-3">Success!</h2>
//               <p className="text-gray-600 mb-8 text-lg">
//                 Found{" "}
//                 <span className="font-bold" style={{ color: platformColors.primary }}>
//                   {newPostsCount}
//                 </span>{" "}
//                 potential {newPostsCount === 1 ? "lead" : "leads"} on {platformName}!
//               </p>

//               <motion.button
//                 whileHover={{ scale: 1.03 }}
//                 whileTap={{ scale: 0.98 }}
//                 onClick={onClose}
//                 style={{
//                   background: platformColors.gradient,
//                   boxShadow: `0 2px 8px rgba(0, 0, 0, 0.3)`,
//                 }}
//                 className="w-full py-3.5 text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg flex items-center justify-center text-lg"
//               >
//                 View Results
//                 <ArrowRight className="ml-2 w-5 h-5" />
//               </motion.button>
//             </div>
//           )}

//           {/* ===== PROGRESS STATE ===== */}
//           {inProgress && (
//             <>
//               <div className="text-center mb-8">
//                 <div className="relative w-32 h-32 mx-auto mb-6">
//                   {/* Background spinner - constant slow rotation */}
//                   <motion.div
//                     animate={{ rotate: 360 }}
//                     transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
//                     style={{
//                       width: "100%",
//                       height: "100%",
//                       position: "absolute",
//                       borderRadius: "100%",
//                       border: `4px dashed ${platformColors.medium}`,
//                       opacity: 0.5,
//                     }}
//                   />

//                   {/* Circle progress */}
//                   <svg className="w-full h-full" viewBox="0 0 100 100">
//                     <circle cx="50" cy="50" r="42" fill="none" stroke="#f0f0f0" strokeWidth="8" />
//                     <motion.circle
//                       cx="50"
//                       cy="50"
//                       r="42"
//                       fill="none"
//                       stroke={platformColors.primary}
//                       strokeWidth="8"
//                       strokeLinecap="round"
//                       initial={{ pathLength: 0 }}
//                       animate={{ pathLength: progress / 100 }}
//                       transition={{ duration: 0.5, ease: "easeInOut" }}
//                       style={{ rotate: -90, transformOrigin: "center" }}
//                     />
//                   </svg>

//                   {/* Inner content */}
//                   <div className="absolute inset-0 flex flex-col items-center justify-center">
//                     <div className="flex items-center mb-1">{platformIcon}</div>
//                     <span className="text-2xl font-bold" style={{ color: platformColors.primary }}>
//                       {Math.round(progress)}%
//                     </span>
//                   </div>
//                 </div>

//                 <h2 className="text-2xl font-bold text-gray-800 mb-2">Analyzing {platformName}</h2>
//                 <p className="text-gray-600 text-lg mb-2">Finding potential customers for your product</p>

//                 {/* Time remaining with animated clock */}
//                 <div className="flex items-center justify-center text-gray-500 mt-2">
//                   <Clock className="w-4 h-4 mr-2" />
//                   <motion.div
//                     key={remainingTime}
//                     initial={{ opacity: 0, y: 5 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     className="flex items-center"
//                   >
//                     <span className="text-sm">Estimated time remaining: </span>
//                     <span className="text-sm font-medium ml-1" style={{ color: platformColors.primary }}>
//                       {formatTime(remainingTime)}
//                     </span>
//                   </motion.div>
//                 </div>
//               </div>

//               {/* Analysis steps */}
//               <div className="mb-8">
//                 {ANALYSIS_STEPS.map((step, index) => (
//                   <div key={index} className="flex items-center mb-4 last:mb-0">
//                     <div
//                       style={{
//                         background: index < activeStep ? platformColors.primary : index === activeStep ? platformColors.light : "#f3f4f6",
//                         borderColor: index < activeStep || index === activeStep ? platformColors.medium : "#e5e7eb",
//                       }}
//                       className="w-8 h-8 rounded-full flex items-center justify-center mr-3 transition-colors duration-300 border"
//                     >
//                       {index < activeStep ? (
//                         <CheckCircle size={16} className="text-white" />
//                       ) : index === activeStep ? (
//                         <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
//                           <Loader size={16} style={{ color: platformColors.primary }} />
//                         </motion.div>
//                       ) : (
//                         <div className="w-2.5 h-2.5 bg-gray-300 rounded-full" />
//                       )}
//                     </div>

//                     <div className="flex-1">
//                       <div className="flex justify-between items-center">
//                         <span
//                           style={{
//                             color:
//                               index < activeStep ? platformColors.primary : index === activeStep ? platformColors.secondary : "#9ca3af",
//                           }}
//                           className="text-base font-medium"
//                         >
//                           {step.label}
//                         </span>
//                         {index < activeStep && (
//                           <span style={{ color: platformColors.primary }} className="text-xs font-medium">
//                             Complete
//                           </span>
//                         )}
//                       </div>

//                       {/* Progress bar for current step */}
//                       {index === activeStep && (
//                         <div className="mt-1.5 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
//                           <motion.div
//                             style={{ background: platformColors.gradient }}
//                             initial={{ width: 0 }}
//                             animate={{
//                               width: `${Math.min(
//                                 100,
//                                 ((progress - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0)) /
//                                   (step.completeAt - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0))) *
//                                   100
//                               )}%`,
//                             }}
//                             transition={{ duration: 0.5 }}
//                             className="h-full"
//                           />
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 ))}
//               </div>

//               {/* Rotating tips */}
//               <AnimatePresence mode="wait">
//                 <motion.div
//                   key={currentTipIndex}
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -10 }}
//                   transition={{ duration: 0.3 }}
//                   style={{
//                     background: platformColors.light,
//                     borderColor: platformColors.medium,
//                   }}
//                   className="p-5 rounded-xl border"
//                 >
//                   <div className="flex items-start">
//                     <div style={{ color: platformColors.primary }} className="mr-4 mt-1 flex-shrink-0">
//                       {ANALYSIS_TIPS[currentTipIndex].icon}
//                     </div>
//                     <div>
//                       <h4 style={{ color: platformColors.primary }} className="font-semibold text-lg mb-1">
//                         {ANALYSIS_TIPS[currentTipIndex].title}
//                       </h4>
//                       <p className="text-gray-700">{ANALYSIS_TIPS[currentTipIndex].text}</p>
//                     </div>
//                   </div>
//                 </motion.div>
//               </AnimatePresence>
//             </>
//           )}
//         </div>
//       </motion.div>
//     </div>
//   );
// };

// export default React.memo(AnalysisProgressModal);

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Loader,
  Search,
  Filter,
  AlertCircle,
  Edit,
  RefreshCw,
  Sparkles,
  Globe, // Using Globe as a generic 'web' icon
  Clock,
  ArrowRight,
  MessageSquare,
  BarChart,
  Trophy,
  Info,
  ThumbsUp,
  Target,
  Brain, // For AI related tips
} from "lucide-react";

// --- Green Theme Palette ---
const theme = {
  primary: "#10B981", // Emerald 500
  secondary: "#059669", // Emerald 600
  light: "#ECFDF5", // Emerald 50
  medium: "#A7F3D0", // Emerald 200
  dark: "#047857", // Emerald 700
  gradient: "linear-gradient(135deg, #10B981, #047857)",
  gradientLight: "linear-gradient(135deg, #ECFDF5, #A7F3D0)",
  textPrimary: "#1f2937", // Gray 800
  textSecondary: "#4b5563", // Gray 600
  textMuted: "#6b7280", // Gray 500
  iconColor: "#10B981",
};

// --- Engaging Content for Rotation ---
const ENGAGING_CONTENT = [
  {
    icon: <Search className="w-6 h-6" color={theme.iconColor} />,
    title: "Scanning Digital Landscapes",
    text: "Our AI is navigating vast online conversations to pinpoint potential leads.",
  },
  {
    icon: <Filter className="w-6 h-6" color={theme.iconColor} />,
    title: "Intelligent Filtering",
    text: "Applying smart filters to remove noise and highlight high-intent signals.",
  },
  {
    icon: <MessageSquare className="w-6 h-6" color={theme.iconColor} />,
    title: "Understanding Context",
    text: "Analyzing the nuances of language to grasp user intent accurately.",
  },
  {
    icon: <BarChart className="w-6 h-6" color={theme.iconColor} />,
    title: "Lead Scoring in Progress",
    text: "Quantifying relevance – each potential lead is scored for a perfect fit.",
  },
  {
    icon: <Target className="w-6 h-6" color={theme.iconColor} />,
    title: "Did you know?",
    text: "Targeting specific keywords helps find leads discussing problems your product solves.",
  },
  {
    icon: <Brain className="w-6 h-6" color={theme.iconColor} />,
    title: "AI @ Work",
    text: "Machine learning models are continuously improving to find better matches over time.",
  },
  {
    icon: <ThumbsUp className="w-6 h-6" color={theme.iconColor} />,
    title: "Optimizing for Quality",
    text: "We prioritize leads showing clear buying intent or pain points you address.",
  },
  {
    icon: <Sparkles className="w-6 h-6" color={theme.iconColor} />,
    title: "Preparing Insights",
    text: "Compiling actionable data and suggesting relevant engagement strategies.",
  },
];

// --- Analysis Steps ---
const ANALYSIS_STEPS = [
  { label: "Initiating Scan", completeAt: 15 },
  { label: "Gathering Conversations", completeAt: 35 },
  { label: "Analyzing Language & Intent", completeAt: 60 },
  { label: "Scoring & Filtering Leads", completeAt: 80 },
  { label: "Generating Actionable Insights", completeAt: 95 },
  { label: "Finalizing Report", completeAt: 100 },
];

const AnalysisProgressModal = ({
  isOpen,
  progress, // Percentage 0-100
  newPostsCount,
  onClose,
  onRetry,
  onEditKeywords,
}) => {
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [remainingTime, setRemainingTime] = useState(180); // Start with 3 minutes
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const contentIntervalRef = useRef(null);

  // Rotate engaging content
  useEffect(() => {
    if (progress < 100 && isOpen) {
      contentIntervalRef.current = setInterval(() => {
        setCurrentContentIndex((prev) => (prev + 1) % ENGAGING_CONTENT.length);
      }, 5000); // Rotate every 5 seconds
    }
    return () => {
      if (contentIntervalRef.current) {
        clearInterval(contentIntervalRef.current);
      }
    };
  }, [progress, isOpen]);

  // Handle completion state & auto-close logic
  useEffect(() => {
    if (progress >= 100) {
      setIsComplete(true);
      if (intervalRef.current) clearInterval(intervalRef.current); // Stop timer
      if (contentIntervalRef.current) clearInterval(contentIntervalRef.current); // Stop tips rotation

      if (newPostsCount > 0) {
        // Auto-close on success after a short delay
        timeoutRef.current = setTimeout(onClose, 3000);
      }
    } else {
      // Reset completion state if progress goes below 100 (e.g., on retry)
      setIsComplete(false);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [progress, newPostsCount, onClose]);

  // Countdown timer effect (Estimate based on progress)
  useEffect(() => {
    if (progress < 100 && isOpen) {
      // Estimate total duration (e.g., 150 seconds avg) and calculate remaining
      const estimatedTotalDuration = 150; // Adjust based on typical times
      const estimatedRemaining = Math.max(
        10, // Minimum 10 seconds display
        Math.ceil(estimatedTotalDuration * (1 - progress / 100))
      );
      setRemainingTime(estimatedRemaining);

      intervalRef.current = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            return 0; // Stay at 0
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [progress, isOpen]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const currentStepIndex = ANALYSIS_STEPS.findIndex((step) => progress < step.completeAt);
  const activeStep = currentStepIndex === -1 ? ANALYSIS_STEPS.length - 1 : currentStepIndex;

  if (!isOpen) return null;

  const hasResults = isComplete && newPostsCount > 0;
  const noResults = isComplete && newPostsCount === 0;
  const inProgress = !isComplete;

  const MotionIconWrapper = ({ children, delay = 0 }) => (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 20, delay }}
      className="w-24 h-24 md:w-28 md:h-28 rounded-full mx-auto mb-6 flex items-center justify-center border-2"
      style={{ background: theme.gradientLight, borderColor: theme.medium }}
    >
      {children}
    </motion.div>
  );

  return (
    <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-md flex items-center justify-center p-4 z-[999]">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        style={{ boxShadow: "0 20px 40px rgba(0,0,0,0.2)", maxWidth: "600px" }} // Increased max width
        className="bg-white rounded-2xl overflow-hidden w-full shadow-2xl"
      >
        {/* Themed Header Bar */}
        <div style={{ background: theme.gradient, height: "8px" }} />

        <div className="p-6 md:p-10">
          {/* ===== NO RESULTS STATE ===== */}
          {noResults && (
            <div className="text-center">
              <MotionIconWrapper>
                <Search color={theme.iconColor} className="w-12 h-12 md:w-14 md:h-14" />
              </MotionIconWrapper>

              <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">No Matches Found This Time</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm md:text-base">
                We couldn't find relevant conversations matching your current keywords. Refining your search might yield better results.
              </p>

              {/* Suggestions Box */}
              <div style={{ background: theme.light, borderColor: theme.medium }} className="border rounded-xl p-4 md:p-5 mb-6 text-left">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center text-base md:text-lg">
                  <Sparkles color={theme.iconColor} className="w-5 h-5 mr-2 flex-shrink-0" />
                  Tips for Better Results
                </h3>
                <ul className="text-sm text-gray-700 space-y-2 md:space-y-3">
                  {/* Improved list styling */}
                  {[
                    "Try broader keywords related to customer pain points.",
                    "Add specific jargon or terms your target audience uses.",
                    "Include phrases indicating problems your product solves.",
                    "Consider alternative platforms if applicable.",
                  ].map((tip, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      className="flex items-start"
                    >
                      <span className="mr-2 mt-0.5 font-bold" style={{ color: theme.primary }}>
                        ›
                      </span>
                      <span>{tip}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <motion.button
                  whileHover={{ scale: 1.03, boxShadow: `0 4px 15px rgba(0,0,0,0.2)` }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onEditKeywords}
                  style={{ background: theme.gradient }}
                  className="px-5 py-3 flex-1 text-white rounded-xl font-medium transition-all duration-200 flex items-center justify-center shadow-md"
                >
                  <Edit size={18} className="mr-2" />
                  Edit Keywords
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onRetry}
                  className="px-5 py-3 flex-1 bg-gray-100 text-gray-800 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  <RefreshCw size={18} className="mr-2 inline-block" />
                  Try Again
                </motion.button>
              </div>
              <p className="text-xs text-gray-400 mt-5">No credits were used for this search.</p>
            </div>
          )}

          {/* ===== SUCCESS STATE ===== */}
          {hasResults && (
            <div className="text-center">
              <MotionIconWrapper delay={0.2}>
                <Trophy color={theme.iconColor} className="w-12 h-12 md:w-16 md:h-16" />
              </MotionIconWrapper>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-2xl md:text-3xl font-bold text-gray-800 mb-3"
              >
                Success! Leads Found.
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-gray-600 mb-8 text-base md:text-lg"
              >
                Found{" "}
                <span className="font-bold" style={{ color: theme.primary }}>
                  {newPostsCount}
                </span>{" "}
                potential {newPostsCount === 1 ? "lead" : "leads"}!
              </motion.p>

              <motion.button
                whileHover={{ scale: 1.03, boxShadow: `0 4px 15px rgba(0,0,0,0.2)` }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                style={{ background: theme.gradient }}
                className="w-full max-w-xs mx-auto py-3 md:py-3.5 text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg flex items-center justify-center text-base md:text-lg shadow-md"
              >
                View Results
                <ArrowRight className="ml-2 w-5 h-5" />
              </motion.button>
            </div>
          )}

          {/* ===== PROGRESS STATE ===== */}
          {inProgress && (
            <>
              <div className="text-center mb-8 md:mb-10">
                {/* Enhanced Progress Indicator */}
                <div className="relative w-32 h-32 md:w-36 md:h-36 mx-auto mb-6">
                  {/* Pulsing Background */}
                  <motion.div
                    animate={{ opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "absolute",
                      borderRadius: "100%",
                      border: `3px dashed ${theme.medium}`,
                    }}
                  />
                  {/* Progress Circle */}
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" fill="none" stroke={theme.light} strokeWidth="8" />
                    <motion.circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke={theme.primary}
                      strokeWidth="8"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: progress / 100 }}
                      transition={{ duration: 0.5, ease: "linear" }} // Linear for smooth progress
                      style={{ rotate: -90, transformOrigin: "center" }}
                    />
                  </svg>
                  {/* Inner Content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <motion.div
                      key={progress} // Animate when progress changes
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                    >
                      <Globe size={28} color={theme.iconColor} className="mb-1" />
                    </motion.div>
                    <motion.span
                      key={`perc-${progress}`} // Key change triggers animation if desired
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-2xl md:text-3xl font-bold"
                      style={{ color: theme.primary }}
                    >
                      {Math.round(progress)}%
                    </motion.span>
                  </div>
                </div>

                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">AI Analysis in Progress...</h2>
                <p className="text-gray-600 text-base md:text-lg mb-3">Finding relevant leads for you.</p>

                {/* Estimated Time Remaining */}
                <div className="flex items-center justify-center text-gray-500 mt-2 text-sm">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>Est. remaining: </span>
                  <motion.span
                    key={remainingTime} // Animate text change
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 1 }}
                    className="font-medium ml-1"
                    style={{ color: theme.primary }}
                  >
                    {formatTime(remainingTime)}
                  </motion.span>
                </div>
              </div>

              {/* Analysis Steps Visualizer */}
              <div className="mb-8 md:mb-10 space-y-4 md:space-y-5">
                {ANALYSIS_STEPS.map((step, index) => {
                  const isStepComplete = index < activeStep;
                  const isStepActive = index === activeStep;
                  const stepProgress = isStepActive
                    ? Math.min(
                        100,
                        ((progress - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0)) /
                          (step.completeAt - (index > 0 ? ANALYSIS_STEPS[index - 1].completeAt : 0))) *
                          100
                      )
                    : isStepComplete
                    ? 100
                    : 0;

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      className="flex items-center"
                    >
                      <motion.div
                        initial={false}
                        animate={{
                          background: isStepComplete ? theme.primary : isStepActive ? theme.light : theme.light,
                          borderColor: isStepComplete ? theme.primary : isStepActive ? theme.primary : theme.medium,
                        }}
                        transition={{ duration: 0.3 }}
                        className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center mr-3 md:mr-4 transition-colors duration-300 border-2 flex-shrink-0`}
                      >
                        <AnimatePresence>
                          {isStepComplete ? (
                            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                              <CheckCircle size={16} className="text-white" />
                            </motion.div>
                          ) : isStepActive ? (
                            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}>
                              <Loader size={14} style={{ color: theme.primary }} />
                            </motion.div>
                          ) : (
                            <div className="w-2 h-2 bg-gray-300 rounded-full" />
                          )}
                        </AnimatePresence>
                      </motion.div>

                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span
                            style={{
                              color: isStepComplete ? theme.primary : isStepActive ? theme.secondary : theme.textMuted,
                            }}
                            className={`text-sm md:text-base font-medium transition-colors duration-300 ${isStepActive ? "font-bold" : ""}`}
                          >
                            {step.label}
                          </span>
                          {isStepComplete && (
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              style={{ color: theme.primary }}
                              className="text-xs font-medium"
                            >
                              Done
                            </motion.span>
                          )}
                        </div>
                        {/* Optional: micro progress bar for active step */}
                        {isStepActive && (
                          <div className="mt-1.5 h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                            <motion.div
                              style={{ background: theme.gradient }}
                              initial={{ width: 0 }}
                              animate={{ width: `${stepProgress}%` }}
                              transition={{ duration: 0.4, ease: "easeInOut" }}
                              className="h-full"
                            />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Rotating Engaging Content */}
              <div className="relative h-28 md:h-24">
                {" "}
                {/* Fixed height to prevent layout shifts */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentContentIndex}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    style={{ background: theme.gradientLight, borderColor: theme.medium }}
                    className="p-4 md:p-5 rounded-xl border absolute w-full"
                  >
                    <div className="flex items-start">
                      <div className="mr-3 md:mr-4 mt-0.5 flex-shrink-0">{ENGAGING_CONTENT[currentContentIndex].icon}</div>
                      <div>
                        <h4 style={{ color: theme.dark }} className="font-semibold text-base md:text-lg mb-1">
                          {ENGAGING_CONTENT[currentContentIndex].title}
                        </h4>
                        <p className="text-gray-700 text-sm md:text-base">{ENGAGING_CONTENT[currentContentIndex].text}</p>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default React.memo(AnalysisProgressModal); // Use memo for performance
